import { toast } from 'react-toastify';
import { createAsyncThunk, createSlice, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import { ICompany } from '../../../../interfaces';
import store from '../../../app/Store';

const VENDOR_MC_URL = `${process.env.REACT_APP_API_PIM_URL}/vendor`;
interface IState {
  data: ICompany;
  loading: boolean;
  done: boolean;
  showError: boolean;
  error: any;
}

const initialState: IState = {
  data: {} as ICompany,
  loading: false,
  done: false,
  error: null,
  showError: false,
};

export const updateCompanyTitle = createAsyncThunk('company-title/update', async (value: String) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  var res: any;
  try {
    res = await axios.post<ICompany>(`${VENDOR_MC_URL}/${vendorId}/update-title`, { value });
  } catch (error: any) {
    res = error?.response;
  }
  return res?.data;
});

export const updateCompanyKepAddress = createAsyncThunk('company-kepaddress/update', async (value: String) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  const res = await axios.post<ICompany>(`${VENDOR_MC_URL}/${vendorId}/update-kepaddress`, { value });
  return res.data;
});

export const updateMinimumBasketAmount = createAsyncThunk('company-minimumbasket/update', async (value: String, { rejectWithValue }: any) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  try {
    const res = await axios.post<ICompany>(`${VENDOR_MC_URL}/${vendorId}/update-minimum-basket-amount`, { value });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response);
  }
});

export const updateCompanyMersisNumber = createAsyncThunk('company-mersisnumber/update', async (value: String) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  const res = await axios.post<ICompany>(`${VENDOR_MC_URL}/${vendorId}/update-mersisnumber`, { value });
  return res.data;
});

export const updateCompanyIban = createAsyncThunk('company-iban/update', async (value: String) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  const res = await axios.post<ICompany>(`${VENDOR_MC_URL}/${vendorId}/update-iban`, { value });
  return res.data;
});

export const getCompanyDetail = createAsyncThunk('company/detail', async () => {
  const {
    auth: { user },
  } = store.getState();
  const res = await axios.get<ICompany>(`${VENDOR_MC_URL}/${user?.vendor?.id}/show`);
  return res.data;
});

export const activateVacation = createAsyncThunk('company-vacation/activate', async (_, { rejectWithValue }: any) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  try {
    await axios.post(`${VENDOR_MC_URL}/${vendorId}/vacation/activate`);
    return { vacationAt: true };
  } catch (err: any) {
    return rejectWithValue(err.response);
  }
});

export const deActivateVacation = createAsyncThunk('company-vacation/deactivate', async (_, { rejectWithValue }: any) => {
  const vendorId = store.getState()?.auth?.user?.vendor?.id;
  try {
    await axios.post(`${VENDOR_MC_URL}/${vendorId}/vacation/deactivate`);
    return { vacationAt: false };
  } catch (err: any) {
    return rejectWithValue(err.response);
  }
});

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setError: (state, action) => {
      state.showError = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IState>) => {
    //
    builder.addCase(getCompanyDetail.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getCompanyDetail.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.done = true;
    });
    builder.addCase(getCompanyDetail.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = 'Hata! Şirket bilgisi bulunamadı.';
    });
    //
    builder.addCase(updateCompanyTitle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateCompanyTitle.fulfilled, (state, action) => {
      if (action.payload?.errorCode) {
        if (action.payload?.errorCode === 'VENDOR_TITLE_EDIT_EXPIRE_DATE') {
          state.error = '15 gün içerisinde mağaza adınınızı bir kere güncelleyebilirsiniz.';
        }
      } else {
        state.data = action.payload;
      }
      state.loading = false;
      state.done = true;
    });
    builder.addCase(updateCompanyTitle.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = 'Hata! Veri Güncellenemedi.';
    });
    //
    builder.addCase(updateCompanyKepAddress.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateCompanyKepAddress.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.done = true;
    });
    builder.addCase(updateCompanyKepAddress.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = 'Hata! Veri Güncellenemedi.';
    });
    //
    builder.addCase(updateCompanyMersisNumber.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateCompanyMersisNumber.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.done = true;
    });
    builder.addCase(updateCompanyMersisNumber.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = 'Hata! Veri Güncellenemedi.';
    });
    //
    builder.addCase(updateMinimumBasketAmount.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateMinimumBasketAmount.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.done = true;
      toast.success('Minimum sepet tutarı başarıya güncellendi.');
    });
    builder.addCase(updateMinimumBasketAmount.rejected, (state, action: any) => {
      state.loading = false;
      state.showError = true;
      state.error = action.payload.data.message;
    });
    //
    builder.addCase(updateCompanyIban.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateCompanyIban.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.done = true;
    });
    builder.addCase(updateCompanyIban.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = 'Hata! Veri Güncellenemedi.';
    });
    //
    builder.addCase(activateVacation.fulfilled, (state, action) => {
      state.loading = false;
      state.data = { ...state.data, ...action.payload };
      state.done = true;
    });
    builder.addCase(activateVacation.rejected, (state, action) => {
      state.loading = false;
      state.showError = true;
      state.error = action.payload;
    });
    //
    builder.addCase(deActivateVacation.fulfilled, (state, action) => {
      state.loading = false;
      state.data = { ...state.data, ...action.payload };
      state.done = true;
    });
    builder.addCase(deActivateVacation.rejected, (state, action) => {
      state.loading = false;
      state.showError = false;
      state.error = action.payload;
    });
  },
});
export const { setError } = companySlice.actions;
export default companySlice.reducer;
